import axios from 'axios';
import config from '../config';
import {
  ComplianceItem,
  ComplianceSyncResponse,
  ResourceType,
} from '../data/types';

const newApiHost = config.apiUrl;
const secretKey = process.env.REACT_APP_SECRET_KEY ? process.env.REACT_APP_SECRET_KEY : '';
const apiKey = process.env.REACT_APP_API_KEY ? process.env.REACT_APP_API_KEY : '';

const axiosInstance = axios.create({
  baseURL: newApiHost,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
});

axiosInstance.defaults.headers.common['HOMECOOK-SECRET-KEY'] = secretKey;
axiosInstance.defaults.headers.common['HOMECOOK-API-KEY'] = apiKey;

const unwrapList = (data: unknown): ComplianceItem[] => {
  if (Array.isArray(data)) {
    return data as ComplianceItem[];
  }
  if (data && typeof data === 'object' && 'body' in data) {
    const body = (data as { body?: ComplianceItem[] }).body;
    return Array.isArray(body) ? body : [];
  }
  if (data && typeof data === 'object' && 'items' in data) {
    const items = (data as { items?: ComplianceItem[] }).items;
    return Array.isArray(items) ? items : [];
  }
  return [];
};

/** Fetch persisted compliance items for a community resource. */
export const getComplianceItems = async (
  communityId: string,
  options?: { resourceId?: string; resourceType?: ResourceType }
): Promise<ComplianceItem[]> => {
  if (!communityId) {
    return [];
  }

  const params = new URLSearchParams();
  if (options?.resourceId) {
    params.set('resource_id', options.resourceId);
  }
  if (options?.resourceType) {
    params.set('resource_type', options.resourceType);
  }

  const query = params.toString();
  const apiUrl = `/community/${communityId}/compliance${query ? `?${query}` : ''}`;

  try {
    const response = await axiosInstance.get(apiUrl);
    return unwrapList(response.data);
  } catch (error) {
    console.error('getComplianceItems failed', error);
    return [];
  }
};

/** Sync or generate compliance items when missing or outdated for the resource location. */
export const syncComplianceItems = async (
  communityId: string,
  payload: { resourceId: string; resourceType?: ResourceType; force?: boolean }
): Promise<ComplianceSyncResponse> => {
  if (!communityId || !payload.resourceId) {
    return { items: [], generated: false };
  }

  const apiUrl = `/community/${communityId}/compliance/sync`;

  try {
    const response = await axiosInstance.post(apiUrl, {
      resource_id: payload.resourceId,
      resource_type: payload.resourceType || 'kitchen',
      force: payload.force || false,
    });
    const data = response.data as ComplianceSyncResponse;
    return {
      items: unwrapList(data?.items ?? data),
      region_note: data?.region_note,
      generated: data?.generated,
      source: data?.source,
    };
  } catch (error) {
    console.error('syncComplianceItems failed', error);
    return { items: [], generated: false };
  }
};

/** Patch a compliance item (status, expiry, etc.). */
export const updateComplianceItem = async (
  communityId: string,
  itemId: string,
  updates: Partial<ComplianceItem>
): Promise<ComplianceItem | null> => {
  if (!communityId || !itemId) {
    return null;
  }

  const apiUrl = `/community/${communityId}/compliance/${itemId}`;

  try {
    const response = await axiosInstance.patch(apiUrl, updates);
    return (response.data?.body ?? response.data) as ComplianceItem;
  } catch (error) {
    console.error('updateComplianceItem failed', error);
    return null;
  }
};

/** Upload a compliance document and mark the item pending verification. */
export const uploadComplianceDocument = async (
  communityId: string,
  itemId: string,
  file: File
): Promise<ComplianceItem | null> => {
  if (!communityId || !itemId || !file) {
    return null;
  }

  const formData = new FormData();
  formData.append('file', file);

  const apiUrl = `/community/${communityId}/compliance/${itemId}/upload`;

  try {
    const response = await axiosInstance.post(apiUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return (response.data?.body ?? response.data) as ComplianceItem;
  } catch (error) {
    console.error('uploadComplianceDocument failed', error);
    return null;
  }
};
